import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select } from "antd";
import useTranslation from "next-translate/useTranslation";

import {
  getProvinceApi,
  getDistrictApi,
  getVillageApi,
  getListAddressApi,
  createAddressApi,
  updateAddressApi,
  deleteAddressApi,
} from "@libs/apis/address";

import { regexPhone } from "@utils/regex";

const AddressPage = ({ isModal = false, setVisible = () => {} }) => {
  const { t } = useTranslation("address");
  const [form] = Form.useForm();

  const provinceValue = Form.useWatch("province", form);
  const districtValue = Form.useWatch("district", form);

  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(true);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [villageOptions, setVillageOptions] = useState([]);

  const [listAddress, setListAddress] = useState([]);
  const [chosenId, setChosenId] = useState(null);
  const [isCreate, setIsCreate] = useState(true); // true => create ===== false => update

  useEffect(() => {
    getListAddressApi({
      limit: 999,
    })
      .then((res) => setListAddress(res?.data?.data))
      .catch((err) => console.log("== err", err));
  }, []);

  useEffect(() => {
    getProvinceApi({
      limit: 999,
    })
      .then((res) =>
        setProvinceOptions(
          res?.data?.data?.docs?.map((item) => ({
            value: item?.id,
            label: item?.name,
            ...item,
          })),
        ),
      )
      .catch((err) => console.log("== err", err));
  }, []);

  useEffect(() => {
    if (provinceValue) {
      setLoading(true);
      getDistrictApi({
        limit: 999,
        filter: `{"province": "${provinceValue}"}`,
      })
        .then((res) =>
          setDistrictOptions(
            res?.data?.data?.docs?.map((item) => ({
              value: item?.id,
              label: item?.name,
              ...item,
            })),
            setLoading(false),
          ),
        )
        .catch((err) => console.log("== err", err));
    }
  }, [provinceValue]);

  useEffect(() => {
    if (districtValue) {
      setLoading(true);
      getVillageApi({
        limit: 999,
        filter: `{"district": "${districtValue}"}`,
      })
        .then((res) =>
          setVillageOptions(
            res?.data?.data?.docs?.map((item) => ({
              value: item?.id,
              label: item?.name,
              ...item,
            })),
            setLoading(false),
          ),
        )
        .catch((err) => console.log("== err", err));
    }
  }, [districtValue]);

  const handleSubmitForm = async (data) => {
    const submitData = {
      phone: data?.phone,
      fullName: data?.name,
      address: data?.address,
      province: data?.province,
      district: data?.district,
      village: data?.village,
    };

    if (isCreate) {
      createAddressApi(submitData)
        .then(() =>
          getListAddressApi({
            limit: 999,
          })
            .then((res) => {
              setListAddress(res?.data?.data);
              form.resetFields();
              setVisible(false);
            })
            .catch((err) => console.log("== err", err)),
        )
        .catch((err) => console.log("== err", err));
    } else {
      updateAddressApi(submitData, chosenId)
        .then(() =>
          getListAddressApi({
            limit: 999,
          })
            .then((res) => {
              setListAddress(res?.data?.data);
              form.resetFields();
              setIsCreate(true);
              setChosenId(null);
            })
            .catch((err) => console.log("== err", err)),
        )
        .catch((err) => console.log("== err", err));
    }
  };

  const handleOnClickDelete = async (item) => {
    deleteAddressApi({ id: item?.id })
      .then(() =>
        getListAddressApi({
          limit: 999,
        })
          .then((res) => {
            setListAddress(res?.data?.data);
            form.resetFields();
          })
          .catch((err) => console.log("== err", err)),
      )
      .catch((err) => console.log("== err", err));
  };

  const handleOnClickEdit = (item) => {
    setChosenId(item?.id);
    setIsCreate(false);
    form.setFieldsValue({
      name: item?.fullName,
      phone: item?.phone,
      address: item?.address,
      province: item?.province?.id,
      district: item?.district?.id,
      village: item?.village?.id,
    });
  };

  const handleOnClickCancel = () => {
    setChosenId(null);
    setIsCreate(true);
    form.resetFields();
  };

  return (
    <div className="address">
      <h3>{t("title")}</h3>

      <div>
        <Form
          form={form}
          onFinish={handleSubmitForm}
          onValuesChange={() =>
            setFormValid(
              form.getFieldsError().some((item) => item.errors.length < 0),
            )
          }
        >
          <div className="address__form">
            <div className="address__form__field">
              <label className="address__form__label">{t("name")}</label>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("requiredName"),
                  },
                ]}
              >
                <Input
                  className="address__form__input"
                  placeholder={t("placeholderName")}
                />
              </Form.Item>
            </div>

            <div className="address__form__field">
              <label className="address__form__label">{t("phone")}</label>
              <Form.Item
                name="phone"
                rules={[
                  {
                    required: true,
                    message: t("requiredPhone"),
                  },
                  {
                    pattern: new RegExp(regexPhone),
                    message: t("rulePhoneType"),
                  },
                  {
                    min: 10,
                    message: t("rulePhoneLength"),
                  },
                  {
                    max: 14,
                    message: t("rulePhoneLength"),
                  },
                ]}
              >
                <Input
                  className="address__form__input"
                  placeholder={t("placeholderPhone")}
                />
              </Form.Item>
            </div>

            <div className="address__form__field">
              <label className="address__form__label">{t("address")}</label>
              <Form.Item
                name="address"
                rules={[
                  {
                    required: true,
                    message: t("requiredAddress"),
                  },
                ]}
              >
                <Input
                  className="address__form__input"
                  placeholder={t("placeholderAddress")}
                />
              </Form.Item>
            </div>

            <div className="address__form__field">
              <label className="address__form__label">{t("province")}</label>
              <Form.Item
                name="province"
                rules={[
                  {
                    required: true,
                    message: t("requireProvince"),
                  },
                ]}
              >
                <Select
                  options={provinceOptions || []}
                  placeholder={t("placeholderProvince")}
                />
              </Form.Item>
            </div>

            <div className="address__form__combo">
              <div className="address__form__field">
                <label className="address__form__label">{t("district")}</label>
                <Form.Item
                  name="district"
                  rules={[
                    {
                      required: true,
                      message: t("requiredDistrict"),
                    },
                  ]}
                >
                  <Select
                    disabled={!provinceValue}
                    loading={loading}
                    options={districtOptions || []}
                    placeholder={t("placeholderDistrict")}
                  />
                </Form.Item>
              </div>
              <div className="address__form__field">
                <label className="address__form__label">{t("village")}</label>
                <Form.Item
                  name="village"
                  rules={[
                    {
                      required: true,
                      message: t("requiredVillage"),
                    },
                  ]}
                >
                  <Select
                    disabled={!provinceValue || !districtValue}
                    loading={loading}
                    options={villageOptions || []}
                    placeholder={t("placeholderVillage")}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="address__form__btn-wrap">
              <Form.Item className="address__form__btn-item">
                <Button
                  className="address__form__btn"
                  htmlType="submit"
                  disabled={formValid}
                >
                  {isCreate ? t("create") : t("update")}
                </Button>
              </Form.Item>

              <Button
                onClick={handleOnClickCancel}
                className="address__form__btn"
              >
                {t("cancel")}
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {!isModal && (
        <>
          <h3>{t("listAddress")}</h3>

          <div className="address__list">
            {listAddress?.map((item, index) => (
              <div key={index} className="address__list__item">
                <div className="address__list__item__content">
                  <div className="address__list__item__field">
                    <span className="address__list__item__field--title">
                      {t("name")}
                    </span>
                    <span className="address__list__item__field--content">
                      {item?.fullName || ""}
                    </span>
                  </div>
                  <div className="address__list__item__field">
                    <span className="address__list__item__field--title">
                      {t("phone")}
                    </span>
                    <span className="address__list__item__field--content">
                      {item?.phone || ""}
                    </span>
                  </div>
                  <div className="address__list__item__field">
                    <span className="address__list__item__field--title">
                      {t("address")}
                    </span>
                    <span className="address__list__item__field--content">
                      {[
                        item?.address,
                        item?.village?.name,
                        item?.district?.name,
                        item?.province?.name,
                      ]?.join(", ")}
                    </span>
                  </div>
                </div>

                <div className="address__list__item__action">
                  <div
                    className="address__list__item__edit address__list__item__btn"
                    onClick={() => handleOnClickEdit(item)}
                  >
                    {t("update")}
                  </div>
                  <div
                    className="address__list__item__delete address__list__item__btn"
                    onClick={() => handleOnClickDelete(item)}
                  >
                    {t("delete")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AddressPage;
